/**
 * Driver Monitor API Service
 * Handles API calls for real-time driver position monitoring
 */

import api from './api';
import type { Route } from '../types';

export interface DriverPosition {
  id: number;
  uid: string;
  driver_id: number;
  lat: number;
  lng: number;
  is_moving: number;
  mock: number;
  speed: number | null;
  activity: {
    phone: string | null;
    task_uid: string | null;
    full_name: string | null;
    task_type: string | null;
    task_status: string | null;
    address_text: string | null;
    merchant_name: string | null;
    platform_code: string | null;
    order_platform_code: string | null;
  } | null;
  event: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface DriverWithPosition {
  driver_id: number;
  driver_uid: string;
  driver_name: string;
  driver_phone: string | null;
  latest_position: DriverPosition | null;
  position_count: number;
  first_position_at: string | null;
  last_position_at: string | null;
}

export interface DriverMonitorStats {
  total_positions: number;
  total_drivers: number;
  total_events: number;
  avg_positions_per_driver: number;
}

/**
 * Get latest positions for all active drivers
 */
export async function getDriverPositions(
  startDate: Date,
  endDate: Date
): Promise<DriverWithPosition[]> {
  const response = await api.get<{ data: DriverWithPosition[] }>('/driver-monitor/positions', {
    params: {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    },
  });
  return response.data.data;
}

/**
 * Get position history for a specific driver
 */
export async function getDriverPositionHistory(
  driverUid: string,
  startDate: Date,
  endDate: Date
): Promise<DriverPosition[]> {
  const response = await api.get<{ data: DriverPosition[] }>(
    `/driver-monitor/positions/${driverUid}/history`,
    {
      params: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
    }
  );
  return response.data.data;
}

/**
 * Get active routes for drivers on a specific date
 */
export async function getDriverRoutes(
  date: Date,
  driverUids?: string[]
): Promise<Route[]> {
  const response = await api.get<{ data: Route[] }>('/driver-monitor/routes', {
    params: {
      date: date.toLocaleDateString('en-CA', { timeZone: 'Asia/Baghdad' }),
      ...(driverUids && driverUids.length > 0 ? { driverUids } : {}),
    },
    // driverUids=a&driverUids=b: Express 5's default parser ignores axios' default driverUids[]=a
    paramsSerializer: { indexes: null },
  });
  return response.data.data;
}

/**
 * Get position statistics for a date range
 */
export async function getDriverMonitorStats(
  startDate: Date,
  endDate: Date
): Promise<DriverMonitorStats> {
  const response = await api.get<{ data: DriverMonitorStats }>('/driver-monitor/stats', {
    params: {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    },
  });
  return response.data.data;
}
